import { useEffect, useMemo, useState } from 'react';

import { confirmAlert } from 'react-confirm-alert';
import styled from '@emotion/styled';
import { toastr } from 'react-redux-toastr';
import { formatDateWithTime, mapServerError } from 'core';
import { useCancelMinorToAdultMigrationMutation } from 'store/candidate/candidateAPI';

import { Button } from '@britishcouncil/react-solas';
import { Divider, Modal, ReadonlyField } from '@britishcouncil/react-solas-ors3';

import { ServerErrors } from '../ServerErrors';

type MinorToAdultMigrationStatusModalProps = {
  startedBy: string;
  startedOn: Date | string;
  email: string;
  candidateId: number;
  show: boolean;
  onExit: (isSuccess: boolean) => void;
}

export const MinorToAdultMigrationStatusModal = ({
  startedBy,
  startedOn,
  email,
  candidateId,
  show,
  onExit,
}: MinorToAdultMigrationStatusModalProps) => {

  const [cancellMinorToAdultMigration, cancellMinorToAdultMigrationData] = useCancelMinorToAdultMigrationMutation();

  useEffect(() => {
    if (cancellMinorToAdultMigrationData.isSuccess) {
      toastr.success('Migration cancelled', 'Minor to adult migration cancelled successfully.');
      onExit(true);
    }
  }, [cancellMinorToAdultMigrationData.isSuccess, onExit]);

  const [confirmingIsInProgress, setConfirmingIsInProgress] = useState(false);

  const serverErrors = useMemo(
    () => (cancellMinorToAdultMigrationData.isError ? mapServerError(cancellMinorToAdultMigrationData.error) : undefined),
    [cancellMinorToAdultMigrationData.error, cancellMinorToAdultMigrationData.isError]
  );

  const submitCancellMinorToAdultMigration = () => {

    setConfirmingIsInProgress(true);
    confirmAlert({
      title: 'Are you sure?',
      message: 'Do you really want to cancel migration process?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            cancellMinorToAdultMigration({candidateId})
            setConfirmingIsInProgress(false);
          }
        },
        {
          label: 'No',
          onClick: () => {
            setConfirmingIsInProgress(false);
          }
        }
      ],
      closeOnEscape: false,
      closeOnClickOutside: false,
    });
  };

  return (
    <Modal
      show={show && !confirmingIsInProgress}
      title="Migration Account status"
      onExit={() => onExit(false)}
      isLoading={cancellMinorToAdultMigrationData.isLoading}

      footer={
        <Footer>
          <Button intent="primary" onClick={submitCancellMinorToAdultMigration} disabled={cancellMinorToAdultMigrationData.isLoading}>
            Cancel Migration
          </Button>
        </Footer>
      }
    >
      <Container>
        <StyledReadonlyField label="Email">{email}</StyledReadonlyField>
        <StyledReadonlyField label="Started By">{startedBy}</StyledReadonlyField>
        <StyledReadonlyField label="Started On">{formatDateWithTime(startedOn)}</StyledReadonlyField>

        <Divider></Divider>

        <p>Click cancellation button to cancel process of migration to use for example new email address.
          User who has been invited will be not able to activate account on which email address invitation has been sent.</p>

        {!!serverErrors && !cancellMinorToAdultMigrationData.isLoading && <ServerErrors errors={serverErrors} />}
      </Container>
    </Modal>
  );
};

const Footer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const Container = styled.div`
  max-width: 632px;
`;

const StyledReadonlyField = styled(ReadonlyField)`
  dt {
    width: 150px;
  }
`;
